import { useEffect, useState, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import { Button } from "@runcheetah/ui";

import { CenteredLoader, InlineNotice } from "@/components/common";
import { ConfirmReviewFlow } from "@/components/onboarding/confirm/confirm-review-flow";
import type { EditableBrandFields } from "@/components/onboarding/confirm/confirm-review-data";
import { getVisualIdentityFieldVisibility } from "@/components/onboarding/confirm/confirm-review-data";
import { trpc } from "@/lib/trpc";

export { getVisualIdentityFieldVisibility };

const LOGO_UPLOAD_ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const;
const LOGO_UPLOAD_ACCEPTED_TYPE_SET = new Set<string>(LOGO_UPLOAD_ACCEPTED_TYPES);
const MAX_LOGO_UPLOAD_BYTES = 5 * 1024 * 1024;

function LoadFailedState({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-6 py-8">
      <InlineNotice tone="danger">
        <FormattedMessage
          id="brandOnboarding.confirm.loadFailedDescription"
          defaultMessage="We could not load the Brand profile right now. You can still continue to the first posts once the data refreshes."
        />
      </InlineNotice>
      <Button onClick={onContinue}>
        <FormattedMessage
          id="brandOnboarding.confirm.continueToFirstPosts"
          defaultMessage="Continue to first posts"
        />
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ConfirmStageShell({ children }: { children: ReactNode }) {
  return <div className="space-y-5">{children}</div>;
}

export function BrandOnboardingConfirmStage({
  brandId,
  sourceDomain,
  onContinue,
}: {
  brandId: string;
  sourceDomain: string;
  onContinue: () => void;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const profileQuery = trpc.brand.getBrandProfile.useQuery({ brandId }, { staleTime: Infinity });
  const voiceQuery = trpc.brand.getBrandVoice.useQuery({ brandId }, { staleTime: Infinity });
  const visualQuery = trpc.brand.getVisualStyle.useQuery({ brandId }, { staleTime: Infinity });
  const logosQuery = trpc.brand.listLogos.useQuery({ brandId }, { staleTime: 5 * 60 * 1000 });
  const mutation = trpc.brand.updateBrandVoice.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.brand.getBrandProfile.invalidate({ brandId }),
        utils.brand.getBrandVoice.invalidate({ brandId }),
        utils.brand.getVisualStyle.invalidate({ brandId }),
        utils.brand.getStrategyRegenerationStatus.invalidate({ brandId }),
      ]);
    },
  });
  const createLogoUpload = trpc.brand.createLogoUpload.useMutation();
  const completeLogoUpload = trpc.brand.completeLogoUpload.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.brand.getVisualStyle.invalidate({ brandId }),
        utils.brand.listLogos.invalidate({ brandId }),
      ]);
    },
  });
  const [readyRevealed, setReadyRevealed] = useState(false);
  const [uploadingLogoName, setUploadingLogoName] = useState<string | null>(null);

  useEffect(() => {
    setReadyRevealed(false);
  }, [brandId]);

  useEffect(() => {
    if (profileQuery.isPending || voiceQuery.isPending || visualQuery.isPending) return;
    const timer = window.setTimeout(() => setReadyRevealed(true), 180);
    return () => window.clearTimeout(timer);
  }, [profileQuery.isPending, voiceQuery.isPending, visualQuery.isPending]);

  const saveFields = async (fields: EditableBrandFields) => {
    try {
      await mutation.mutateAsync({ brandId, fields });
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandOnboarding.confirm.saveFailed",
          defaultMessage: "Could not save changes",
        }),
      );
      throw new Error("save_failed");
    }
  };

  const uploadLogo = async (file: File) => {
    if (!LOGO_UPLOAD_ACCEPTED_TYPE_SET.has(file.type)) {
      toast.error(
        intl.formatMessage({
          id: "brandOnboarding.confirm.logoUploadUnsupported",
          defaultMessage: "Upload a PNG, JPG, WebP, or SVG logo.",
        }),
      );
      return;
    }

    if (file.size > MAX_LOGO_UPLOAD_BYTES) {
      toast.error(
        intl.formatMessage({
          id: "brandOnboarding.confirm.logoUploadTooLarge",
          defaultMessage: "Logo files must be 5 MB or smaller.",
        }),
      );
      return;
    }

    setUploadingLogoName(file.name);
    try {
      const upload = await createLogoUpload.mutateAsync({
        brandId,
        fileName: file.name,
        contentType: file.type as (typeof LOGO_UPLOAD_ACCEPTED_TYPES)[number],
        sizeBytes: file.size,
      });
      const response = await fetch(upload.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": upload.contentType },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Logo upload failed with status ${response.status}`);
      }

      await completeLogoUpload.mutateAsync({
        brandId,
        objectKey: upload.objectKey,
        fileName: file.name,
        contentType: upload.contentType,
        sizeBytes: file.size,
        makePrimary: true,
        regenerateVisualIdentity: true,
      });
      toast.success(
        intl.formatMessage({
          id: "brandOnboarding.confirm.logoUploaded",
          defaultMessage: "Logo uploaded",
        }),
      );
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandOnboarding.confirm.logoUploadFailed",
          defaultMessage: "Could not upload the logo. Try again.",
        }),
      );
    } finally {
      setUploadingLogoName(null);
    }
  };

  if (profileQuery.isPending || voiceQuery.isPending || visualQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "brandOnboarding.confirm.loadingExtractedData",
          defaultMessage: "Loading Brand profile...",
        })}
      />
    );
  }

  if (!profileQuery.data || !voiceQuery.data || !visualQuery.data) {
    return <LoadFailedState onContinue={onContinue} />;
  }

  const primaryLogo = logosQuery.data?.logos.find((logo) => logo.isPrimary) ?? null;
  const logoUploadPending =
    createLogoUpload.isPending || completeLogoUpload.isPending || uploadingLogoName !== null;

  return (
    <ConfirmStageShell>
      <ConfirmReviewFlow
        sourceDomain={sourceDomain}
        profile={profileQuery.data}
        voice={voiceQuery.data}
        visual={visualQuery.data}
        primaryLogo={primaryLogo}
        allRevealed={readyRevealed}
        mutationPending={mutation.isPending || logoUploadPending}
        logoUploadPending={logoUploadPending}
        uploadingLogoName={uploadingLogoName}
        onSave={saveFields}
        onUploadLogo={uploadLogo}
        onContinue={onContinue}
      />
    </ConfirmStageShell>
  );
}
